import { Dispatch, MutableRefObject, useEffect } from "react";
import { Location } from "react-router-dom";
import { getCompanyTenantId } from "../../../../utils/cacheStorage";
import { ManageRecipeVariantAction } from "./manageRecipeVariantReducer";

type UseManageRecipeVariantEffectsParams = {
  recipeId: any;
  dispatch: Dispatch<ManageRecipeVariantAction>;
  hasOpenedNewRecipeModalRef: MutableRefObject<boolean>;
  recipeData: any;
  location: Location;
  isRecipeDataLoading: boolean;
  isFetching: boolean;
  isLoadingStaticRecipeIngredients: boolean;
  debouncedUpdateIngredientPercentage: { cancel: () => void };
};

export const useManageRecipeVariantEffects = ({
  recipeId,
  dispatch,
  hasOpenedNewRecipeModalRef,
  recipeData,
  location,
  isRecipeDataLoading,
  isFetching,
  isLoadingStaticRecipeIngredients,
  debouncedUpdateIngredientPercentage
}: UseManageRecipeVariantEffectsParams) => {
  useEffect(() => {
    hasOpenedNewRecipeModalRef.current = false;
  }, [recipeId, hasOpenedNewRecipeModalRef]);

  useEffect(() => {
    const fetchTenantId = async () => {
      const id = await getCompanyTenantId();
      dispatch({ type: "SET_TENANT", tenantId: id || "" });
    };
    fetchTenantId();
  }, [dispatch]);

  useEffect(() => {
    if (!recipeData) {
      return;
    }

    const nextVariants = recipeData.variants || [];
    dispatch({ type: "SET_VARIANTS", variants: nextVariants });

    if (!recipeData.originalRecipe) {
      dispatch({ type: "SET_IS_NEW_RECIPE", isNewRecipe: true });
      if (!hasOpenedNewRecipeModalRef.current) {
        dispatch({ type: "OPEN_MODAL", modal: "ingredientVisibleModal" });
        hasOpenedNewRecipeModalRef.current = true;
      }
      return;
    }

    dispatch({ type: "SET_IS_NEW_RECIPE", isNewRecipe: false });
    hasOpenedNewRecipeModalRef.current = false;
  }, [recipeData, dispatch, hasOpenedNewRecipeModalRef]);

  useEffect(() => {
    if (
      isRecipeDataLoading ||
      isFetching ||
      isLoadingStaticRecipeIngredients ||
      !location.state?.scrollToResults
    ) {
      return;
    }

    dispatch({ type: "SET_SCROLL_TO_BOTTOM", scrollToBottom: true });
  }, [
    dispatch,
    isRecipeDataLoading,
    isFetching,
    isLoadingStaticRecipeIngredients,
    location.state?.scrollToResults
  ]);

  useEffect(() => {
    return () => {
      debouncedUpdateIngredientPercentage.cancel();
    };
  }, [debouncedUpdateIngredientPercentage]);
};
